import axios, { AxiosResponse } from 'axios';
import { SESSION_CORRELATION_ID, getWindow } from '../utils/general';
import {
  RETURN_SERVICE_ELIGIBILITY,
  RETURN_SERVICE_ELIGIBILITY_V2,
  RETURN_METHODS_PATH,
  UPS_GROUPING_PATH,
  PRINT_RETURN_SHIPPING_LABEL_PATH,
  POLICY_VALIDATOR,
  REFUND_ESTIMATION,
  SS_RETURN_CREATE,
  FINALIZE_SORIS_RETURN,
  VALIDATE_ADDRESS,
  VALIDATE_ZIPCODE,
  ORDER_VERSION,
  APRISS_FINALIZE,
  SCHEDULED_PICKUPS,
} from '../constants/paths';
import { apiB2B } from './axios-client';
import {
  IReturnEligibilityRequest,
  IReturnEligibilityV2Request,
  IReturnEligibilityV2Response,
  IReturnMethodsRequest,
  IReturnMethodsResponse,
  IUpsGroupingRequest,
  IUpsGroupingResponse,
  IReturnShippingLabelRequest,
  IRefundRequest,
  IPolicyValidatorRequest,
  ICreateReturnRequestBody,
  ISorisFinalizeRequest,
  IValidateAddressRequest,
  IValidateZipCodeRequest,
  IPolicyValidatorResponse,
  OrderVersionRequest,
  OrderVersionResponse,
  AprissFinalizeRequest,
  IScheduledPickupsRequest,
} from '../models/api';
import { SSR_SESSION_STORAGE_KEY } from '../constants/general';
import { OrderDetailsDataSessionObject } from '../models/returns';

/**
 * Builds axios instance with correlation id header and 401 interceptors
 * @returns axios instance with correlation id and interceptors
 */
const getDefaultAxiosConfig = async () => {
  let xAuthGuestToken: string | null = null;
  if (getWindow()) {
    const windowData = getWindow()?.sessionStorage.getItem(
      SSR_SESSION_STORAGE_KEY,
    );
    if (windowData) {
      const parsedData = JSON.parse(
        windowData,
      )?.[0] as OrderDetailsDataSessionObject;
      xAuthGuestToken = parsedData?.xAuthGuestToken;
    }
  }
  const headers = {
    ...apiB2B.defaults.headers,
    CorrelationID: SESSION_CORRELATION_ID,
    'X-Auth-GuestToken': xAuthGuestToken,
  };
  const defaultAxiosInstance = axios.create({
    ...apiB2B.defaults,
    headers,
  });
  defaultAxiosInstance.interceptors.response = apiB2B.interceptors.response;
  return defaultAxiosInstance;
};

const defaultAxiosConfig = getDefaultAxiosConfig();

export const ReturnEligibilityAPI = (requestData: IReturnEligibilityRequest) =>
  defaultAxiosConfig.then((axiosInstance) =>
    axiosInstance.post(RETURN_SERVICE_ELIGIBILITY, requestData),
  );

// ROS v2 eligibility. Inert until wired behind `isROSEnabled()`;
// BE endpoint not built yet — build-to-contract only.
export const ReturnEligibilityV2API = (
  requestData: IReturnEligibilityV2Request,
): Promise<AxiosResponse<IReturnEligibilityV2Response>> =>
  defaultAxiosConfig.then((axiosInstance) =>
    axiosInstance.post<IReturnEligibilityV2Response>(
      RETURN_SERVICE_ELIGIBILITY_V2,
      requestData,
    ),
  );

// Return Methods, fronting the Return Options Service. Inert until wired behind
// `isROSEnabled()`; BE endpoint not built yet — build-to-contract only.
export const ReturnMethodsAPI = (
  requestData: IReturnMethodsRequest,
): Promise<AxiosResponse<IReturnMethodsResponse>> =>
  defaultAxiosConfig.then((axiosInstance) =>
    axiosInstance.post<IReturnMethodsResponse>(
      RETURN_METHODS_PATH,
      requestData,
    ),
  );

// UPS grouping, fronting the shipping service's dry-run consolidation check.
// Reserves nothing and persists nothing, so it is safe to call repeatedly —
// confirmed by the backend against the service's own implementation, and the
// two-phase design (group at review, submit at create) rests on it. Inert until
// wired behind `isROSEnabled()`; BE endpoint not built yet — build-to-contract
// only.
export const UpsGroupingAPI = (
  requestData: IUpsGroupingRequest,
): Promise<AxiosResponse<IUpsGroupingResponse>> =>
  defaultAxiosConfig.then((axiosInstance) =>
    axiosInstance.post<IUpsGroupingResponse>(UPS_GROUPING_PATH, requestData),
  );

export const SSReturnCreateAPI = (requestData: ICreateReturnRequestBody) =>
  defaultAxiosConfig.then((axiosInstance) =>
    axiosInstance.post(SS_RETURN_CREATE, requestData),
  );

export const ScheduledPickupsAPI = (requestData: IScheduledPickupsRequest) =>
  defaultAxiosConfig.then((axiosInstance) =>
    axiosInstance.post(SCHEDULED_PICKUPS, requestData),
  );

export const PolicyValidatorAPI = (
  requestData: IPolicyValidatorRequest,
): Promise<AxiosResponse<IPolicyValidatorResponse>> =>
  defaultAxiosConfig.then((axiosInstance) =>
    axiosInstance.post<IPolicyValidatorResponse>(POLICY_VALIDATOR, requestData),
  );

export const ReturnShippingLabelAPI = (
  requestData: IReturnShippingLabelRequest,
) =>
  defaultAxiosConfig.then((axiosInstance) =>
    axiosInstance.post(PRINT_RETURN_SHIPPING_LABEL_PATH, requestData),
  );

export const RefundEstimationAPI = (requestData: IRefundRequest) =>
  defaultAxiosConfig.then((axiosInstance) =>
    axiosInstance.post(REFUND_ESTIMATION, requestData),
  );

export const FinalizeSorisReturnAPI = (requestData: ISorisFinalizeRequest) =>
  defaultAxiosConfig.then((axiosInstance) =>
    axiosInstance.post(FINALIZE_SORIS_RETURN, requestData),
  );

export const ValidateAddressAPI = (requestData: IValidateAddressRequest) =>
  defaultAxiosConfig.then((axiosInstance) =>
    axiosInstance.post(VALIDATE_ADDRESS, requestData),
  );

export const ValidateZipCodeAPI = (requestData: IValidateZipCodeRequest) =>
  defaultAxiosConfig.then((axiosInstance) =>
    axiosInstance.post(VALIDATE_ZIPCODE, requestData),
  );

export const OrderVersionAPI = (
  requestData: OrderVersionRequest,
): Promise<AxiosResponse<OrderVersionResponse>> =>
  defaultAxiosConfig.then((axiosInstance) =>
    axiosInstance.post(ORDER_VERSION, requestData),
  );

export const AprissFinalizeAPI = (requestData: AprissFinalizeRequest) =>
  defaultAxiosConfig.then((axiosInstance) =>
    axiosInstance.post(APRISS_FINALIZE, requestData),
  );
